import { DropDownChevronDownIcon } from 'assets/icons';
import { Popover } from '@headlessui/react';
import { FC, useMemo, useState } from 'react';
import { ServiceAreaInfo } from 'containers/jobs/jobs.model';
import { metersToRoundedMiles } from 'utils/commonUtils';
import { CustomButton } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import ServiceAreaCreateModal from './ServiceAreaCreateModal';
import { CreateServiceAreaFormData } from 'containers/settings/service-areas/serviceAreas.model';
import { selectCurrentTeamJobPermissions } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';

interface ServiceAreaDropdownProps {
  serviceAreas: ServiceAreaInfo[];
  selectedArea: ServiceAreaInfo | null;
  onSelectArea: (area: ServiceAreaInfo) => void;
  isLoading: boolean;
  onCreateServiceArea?: (data: CreateServiceAreaFormData) => void;
  isLoadingCreateServiceArea?: boolean;
  isLatLngZero?: boolean;
}

const ServiceAreaDropdown: FC<ServiceAreaDropdownProps> = ({
  serviceAreas,
  selectedArea,
  onSelectArea,
  isLoading,
  onCreateServiceArea,
  isLoadingCreateServiceArea,
  isLatLngZero
}) => {
  const { editJobSettings } = useAppSelector(selectCurrentTeamJobPermissions);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const separatedServiceAreas = useMemo(() => {
    const withinAreas = serviceAreas?.filter(area => area.isEnclosing);
    const otherAreas = serviceAreas?.filter(area => !area.isEnclosing);
    return { withinAreas, otherAreas };
  }, [serviceAreas]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSaveServiceArea = (data: CreateServiceAreaFormData) => {
    if (onCreateServiceArea) {
      onCreateServiceArea(data);
    }
  };

  const renderServiceAreaItem = (area: ServiceAreaInfo, close: () => void) => {
    const isSelected = selectedArea?.serviceAreaId === area.serviceAreaId;

    return (
      <div
        key={area.serviceAreaId}
        tabIndex={0}
        aria-label={`Select ${area.serviceAreaName}`}
        className={`cursor-pointer px-4 py-2 text-17px text-primaryText outline-none hover:bg-secondaryBtn ${
          isSelected ? 'font-semibold text-primaryText' : 'font-medium'
        }`}
        onClick={() => {
          onSelectArea(area);
          close();
        }}
        role="option"
        aria-selected={isSelected}>
        {area.serviceAreaName}
        {area.distance &&
          !isLatLngZero &&
          ` (${
            area.isEnclosing
              ? `${metersToRoundedMiles(area.distance)} miles from center`
              : `${metersToRoundedMiles(area.distance)} miles away`
          })`}
      </div>
    );
  };

  const anchorComponent = () => (
    <div
      tabIndex={0}
      aria-label="Choose service area"
      className="flex w-full cursor-pointer items-center justify-between rounded bg-secondaryBtn px-5 py-4 outline-none"
      role="button">
      <span
        className={`text-17px text-primaryText ${selectedArea ? 'text-primaryText' : 'text-accentText opacity-70'}`}>
        {selectedArea?.serviceAreaName || ''}
        {selectedArea?.distance &&
          !isLatLngZero &&
          ` (${
            selectedArea?.isEnclosing
              ? `${metersToRoundedMiles(selectedArea.distance)} miles from center`
              : `${metersToRoundedMiles(selectedArea.distance)} miles away`
          })`}
      </span>
      <DropDownChevronDownIcon className="h-[8px] w-[14px] text-gray-400" aria-hidden="true" />
    </div>
  );

  return (
    <div className="w-full">
      <Popover className="relative">
        {({ close }) => (
          <>
            <Popover.Button disabled={isLoading} className="w-full focus:outline-none">
              {anchorComponent()}
            </Popover.Button>

            <Popover.Panel className="absolute z-10 mt-2 w-full min-w-[320px] rounded-md bg-white shadow-lg">
              <div className="max-h-[312px] overflow-y-auto py-2">
                {editJobSettings && (
                  <>
                    <CustomButton
                      className="h-7 w-full justify-start px-4 py-1"
                      type={ButtonType.GHOST}
                      childrenContainerStyles="text-17px"
                      onClick={() => {
                        close();
                        handleOpenModal();
                      }}
                      id="create-service-area-button">
                      + Create service area
                    </CustomButton>
                    <div className="my-2 mx-4 border-b border-secondaryBtn" />
                  </>
                )}

                {/* Within Service Areas Section */}
                {separatedServiceAreas.withinAreas.length > 0 && (
                  <>
                    <div className="px-4 py-1 text-sbase font-medium text-accentText">
                      Within {separatedServiceAreas.withinAreas.length} service areas
                    </div>
                    {separatedServiceAreas.withinAreas.map(area => renderServiceAreaItem(area, close))}
                  </>
                )}

                {/* Other Service Areas Section */}
                {separatedServiceAreas.otherAreas.length > 0 && (
                  <>
                    <div className="px-4 py-1 text-sbase font-medium text-accentText">Other service areas</div>
                    {separatedServiceAreas.otherAreas.map(area => renderServiceAreaItem(area, close))}
                  </>
                )}
              </div>
            </Popover.Panel>
          </>
        )}
      </Popover>

      <ServiceAreaCreateModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveServiceArea}
        isLoading={isLoadingCreateServiceArea}
      />
    </div>
  );
};

export default ServiceAreaDropdown;
